import { GiPathDistance } from "react-icons/gi";
import { imageWithCaption, videoWithPosterImage } from "../objects";
import { multilingualArrayBlockContent } from "../base";
import { metadataWithoutParent } from "../page-fragments";
import { defineType } from "sanity";
import multilingualArrayString, {
  multilingualStringPreview
} from "../base/multilingualArrayString";

export default defineType({
  name: "cruise",
  title: "Cruise",
  type: "document",
  orderings: [
    {
      title: "Cruise Code",
      name: "cruiseCodeAsc",
      by: [{ field: "data.cruiseCode", direction: "asc" }]
    }
  ],
  icon: GiPathDistance,
  fieldsets: [
    { name: "promotions", title: "Promotions" },
    { name: "features", title: "Features" },
    { name: "specialOffers", title: "Special Offers" },
    {
      name: "blog",
      title: "Blog",
      options: { collapsible: true, collapsed: true }
    }
  ],
  groups: [
    {
      name: "seo",
      title: "SEO, Routing & Tracking"
    }
  ],
  preview: {
    select: {
      cruiseCode: "data.cruiseCode",
      departurePort: "data.departurePort.name",
      arrivalPort: "data.arrivalPort.name",
      media: "cardImage",
      departureDate: "data.departureDate",
      cruiseCodeCopy: "cruiseCode",
      cruiseName: "data.cruiseName",
      overrideCruiseName: "overrideCruiseName",
      id: "_id"
    },
    prepare({
      cruiseCode,
      departurePort,
      arrivalPort,
      media,
      departureDate,
      cruiseCodeCopy,
      cruiseName,
      overrideCruiseName,
      id
    }) {
      const departurePortStr = multilingualStringPreview(departurePort);
      const arrivalPortStr = multilingualStringPreview(arrivalPort);
      const overrideCruiseNameStr =
        multilingualStringPreview(overrideCruiseName);
      return {
        title:
          cruiseCode ||
          `(deleted cruise) ${cruiseCodeCopy || id.replace("cruise-", "id: ")}`,
        subtitle: buildCruisePreviewSubtitle({
          departureDate,
          departurePort: departurePortStr,
          arrivalPort: arrivalPortStr,
          cruiseName: cruiseName,
          overrideCruiseName: overrideCruiseNameStr
        }),
        media
      };
    }
  },
  fields: [
    {
      name: "data",
      title: "Data",
      type: "reference",
      to: [{ type: "cruiseData" }],
      readOnly: true
    },
    {
      name: "cardImage",
      title: "Card Image",
      type: "image",
      options: {
        hotspot: true
      },
      validation: (Rule) => Rule.required()
    },
    {
      name: "specialType",
      title: "Special Type",
      type: "string",
      options: {
        list: [
          { title: "Top Cruise", value: "TopCruise" },
          { title: "Antarctica Bridge", value: "AntarcticaBridge" }
        ]
      }
    },
    {
      name: "slideshow",
      title: "Slideshow",
      type: "array",
      of: [
        {
          type: imageWithCaption.name
        },
        {
          type: videoWithPosterImage.name
        }
      ]
    },
    {
      name: "keywords",
      title: "Searchable Keywords",
      type: "array",
      of: [
        {
          type: "multilingualArrayString"
        }
      ]
    },
    {
      name: "overrideCruiseName",
      title: "Override Cruise Market Name",
      description:
        "This field overrides the “Cruise Market Name” field in the cruise “Data” document",
      type: multilingualArrayString.name
    },
    {
      name: "hideMarketCruiseName",
      title: "Hide Cruise Market name",
      description:
        "Hide the cruise market name and always show the old port to port name",
      type: "boolean"
    },
    {
      name: "itineraryDescription",
      title: "Itinerary Description",
      description: "Description text to appear below 'Itinerary & Excursions'",
      type: multilingualArrayBlockContent.name
    },
    {
      name: "showCovidProcedure",
      title: "Show Covid procedure",
      description:
        "Show the standard Covid procedure message (unless overridden below)",
      type: "boolean"
    },
    {
      name: "overrideCovidProcedure",
      title: "Override COVID Procedure",
      description:
        "if empty, the standard COVID procedure text and link for the ship will be used",
      type: multilingualArrayBlockContent.name
    },
    {
      name: "overrideItineraryDisclaimer",
      title: "Override Itinerary Disclaimer",
      description: "if empty, the standard disclaimer will be used",
      type: multilingualArrayBlockContent.name
    },
    {
      name: "metadata",
      title: "SEO, Routing & Tracking",
      type: metadataWithoutParent.name,
      group: "seo"
    },
    {
      name: "cruiseCode",
      title: "Cruise Code",
      type: "string",
      readOnly: true
    },
    {
      name: "arrivalDate",
      title: "Arrival Date",
      type: "string",
      readOnly: true,
      hidden: true
    },
    {
      name: "mapOverride",
      title: "Map Override (optional, overrides image from ECM)",
      type: "image",
      options: {
        hotspot: true
      }
    },
    {
      name: "events",
      title: "Events",
      type: "array",
      of: [{ type: "cruiseEvent" }]
    },
    {
      name: "expeditionTeam",
      title: "Expedition Team",
      type: "array",
      of: [
        {
          type: "reference",
          to: [{ type: "expeditionTeamMember" }]
        }
      ]
    },
    {
      name: "prePostEmotionalSliderImage",
      title: "Pre & Post Cruise Emotional Slider Background Image",
      description:
        "Overrides the default background image for the pre & post cruise section",
      type: "image",
      options: { hotspot: true }
    },
    {
      name: "blog",
      title: "Blog Posts",
      type: "blogSection",
      fieldset: "blog"
    }
  ]
});

type BuildCruisePreviewSubtitleProps = {
  departureDate?: string;
  departurePort?: string;
  arrivalPort?: string;
  cruiseName?: string;
  overrideCruiseName?: string;
};
const buildCruisePreviewSubtitle = ({
  departureDate,
  departurePort,
  arrivalPort,
  cruiseName,
  overrideCruiseName
}: BuildCruisePreviewSubtitleProps) => {
  const noShopApiDataPrefix =
    departurePort && arrivalPort ? undefined : "(no Shop API data)";
  if (!overrideCruiseName && noShopApiDataPrefix) return noShopApiDataPrefix;

  if (overrideCruiseName)
    return `${noShopApiDataPrefix ?? departureDate} - ${overrideCruiseName}`;

  return cruiseName
    ? `${departureDate} - ${cruiseName}`
    : `${departureDate} - ${departurePort} to ${arrivalPort}`;
};
